/**
 * Property domain helpers
 * Pure functions with no external dependencies.
 */

import { formatStreetAddress } from './address.js';

/** A display address for a property record or cached property snapshot. */
export function formatPropertyAddress(property) {
  const address = property?.address;
  if (typeof address === 'string' && address.trim()) return address.trim();
  if (address && typeof address === 'object') {
    const street = formatStreetAddress(address);
    if (street) return street;
    if (typeof address.label === 'string' && address.label.trim()) return address.label.trim();
  }
  const fallback = property?.details?.address || property?.addressFormatted;
  return typeof fallback === 'string' && fallback.trim() ? fallback.trim() : null;
}

export function normalizePropertyUrl(url) {
  if (!url) return url;
  // Update Axis URL's
  // https://www.axisgis.com/Old_Orchard_BeachME/Docs/Batch/Vision_Property_Card/4518.pdf
  // https://api.axisgis.com/node/axisapi/document-view/Old_Orchard_BeachME?path=Docs/Batch/Vision_Property_Card/4518.pdf
  return url.replace(
    /www\.axisgis\.com\/(.*)\/Docs\/Batch\/Vision_Property_Card\/(.*)\.pdf/,
    'api.axisgis.com/node/axisapi/document-view/$1?path=Docs/Batch/Vision_Property_Card/$2.pdf'
  );
}

export function normalizePropertyImageUrl(imageUrl) {
  let url = imageUrl;
  if (!url) return url;

  // 3/28/24 Jeodonnell upgraded their website recently and switched to S3 for images.
  url = url.replace('https://jeodonnell.com/cama_files/', 'https://jeo-cama-files.s3.amazonaws.com/');

  // 5/1/24 OOB CAMA images moved to axisgis api.
  url = url.replace(
    /www\.axisgis\.com\/(.*)\/(Docs\/.*\.jpg)/i,
    'api.axisgis.com/node/axisapi/document-view/$1?path=$2'
  );

  // 8/6/24 Change on mygovnow URL format from http to https.
  url = url.replace(
    /http:\/\/www\.mygovnow\.com/,
    'https://www.mygovnow.com'
  );

  // 3/12/25 Redfin images no longer work.
  if (url.match(/ssl\.cdn-redfin\.com\/photo/)) {
    return null;
  }

  return url;
}

export function computePropertyImageUrls({ realtorDetails, details, data }) {
  const done = {};
  const rawUrls = [
    ...((realtorDetails && realtorDetails.imageUrls) || []),
    ...((details && details.imageUrls) || []),
    ...((data && data.imageUrls) || []),
    ...(data && data.imageUrl ? [data.imageUrl] : [])
  ];

  return rawUrls
    .filter(url => {
      if (!url) return false;
      if (url.match(/ssl\.cdn-redfin\.com\/photo/)) return false;
      const isNew = !done[url];
      done[url] = true;
      return isNew;
    })
    .map(normalizePropertyImageUrl)
    .filter(url => !!url);
}

export function computePropertyImageUrl({
  firstRealtorImageUrl,
  firstAssessorImageUrl,
  dataImageUrl
}) {
  const url = firstRealtorImageUrl || firstAssessorImageUrl || dataImageUrl;
  return normalizePropertyImageUrl(url);
}

// List photos use assessor images; realtor galleries remain available in detail views.
export function getPropertyPrimaryImageUrl(data = {}) {
  return computePropertyImageUrl({
    firstAssessorImageUrl: data.details?.imageUrls?.[0] || data.imageUrls?.[0],
    dataImageUrl: data.imageUrl
  });
}

export function computePropertyAddressLines({ addressFormatted, place, city }) {
  const lines = [];
  if (addressFormatted) {
    lines.push(addressFormatted);
  }
  if (place) {
    lines.push(`${place.city}, ${place.stateCode || place.state} ${place.zipCode}`.trim());
  } else if (city) {
    lines.push(`${city.name}, ${city.state} ${city.zipCode}`.trim());
  }
  return lines;
}

/**
 * Build a property snapshot object suitable for embedding in a license document.
 *
 * @param {Object} params
 * @param {string} params.id - Property ID
 * @param {string} params.address - Property address string
 * @param {Object} params.details - Property details object
 * @param {Object} params.latlng - Property lat/lng coordinates
 * @param {string} params.url - Property URL
 * @param {string|null} params.thumbnailUrl - Public list thumbnail, when generated
 * @returns {Object} Snapshot object
 */
export function buildPropertySnapshot({ id, address, details, latlng, url, thumbnailUrl }) {
  return {
    id,
    address,
    details: details ? {
      address: details.address,
      imageUrls: details.imageUrls,
      url: details.url,
      owners: details.owners,
    } : undefined,
    latlng,
    url,
    ...(thumbnailUrl !== undefined ? { thumbnailUrl } : {})
  };
}

/**
 * Check if a license's embedded property snapshot is out of sync.
 *
 * @param {string|null} licensePropertyId - The propertyId field on the license
 * @param {string|null} snapshotPropertyId - The id field in the embedded property snapshot
 * @returns {boolean} True if out of sync (needs update)
 */
export function isPropertySnapshotOutOfSync(licensePropertyId, snapshotPropertyId) {
  return Boolean(licensePropertyId && licensePropertyId !== snapshotPropertyId);
}

/**
 * Compute which fields need to be deleted when updating a property snapshot.
 * Returns an array of field names that exist in the current snapshot but not in the new one.
 *
 * @param {Object|null} currentSnapshot - The current embedded property snapshot
 * @param {Object|null} newSnapshot - The new snapshot to apply
 * @returns {string[]} Array of field names to delete
 */
export function computeSnapshotFieldsToDelete(currentSnapshot, newSnapshot) {
  if (!currentSnapshot || !newSnapshot) {
    return [];
  }
  return Object.keys(currentSnapshot).filter(field => !newSnapshot.hasOwnProperty(field));
}

// ============================================================================
// Validation helpers (for checking data integrity)
// ============================================================================

/**
 * Validation issue types for property snapshots.
 */
export const SnapshotValidationIssue = Object.freeze({
  MISSING_SNAPSHOT: 'missing_snapshot',
  WRONG_PROPERTY_ID: 'wrong_property_id',
  STALE_DATA: 'stale_data',
  ORPHANED_SNAPSHOT: 'orphaned_snapshot'
});

/**
 * Deep equality check for snapshot fields (handles nested objects).
 * @private
 */
function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

/**
 * Compute which fields in a snapshot differ from the source property.
 *
 * @param {Object|null} snapshot - The embedded snapshot
 * @param {Object|null} property - The source property data
 * @returns {string[]} Array of field names that differ
 */
export function computeSnapshotDrift(snapshot, property) {
  if (!snapshot || !property) {
    return [];
  }

  const expectedSnapshot = buildPropertySnapshot({
    id: property.id,
    address: property.address,
    details: property.details,
    latlng: property.latlng,
    url: property.url,
    thumbnailUrl: property.thumbnailUrl
  });

  const driftingFields = [];

  // Check each field for drift
  for (const field of ['id', 'address', 'latlng', 'url', 'details', 'thumbnailUrl']) {
    if (!deepEqual(snapshot[field], expectedSnapshot[field])) {
      driftingFields.push(field);
    }
  }

  return driftingFields;
}

/**
 * Validate a property snapshot against its source property.
 *
 * @param {Object} params
 * @param {string|null} params.propertyId - The propertyId field on the document (license/listing)
 * @param {Object|null} params.snapshot - The embedded property snapshot
 * @param {Object|null} params.property - The source property data (null if property doesn't exist)
 * @returns {{
 *   valid: boolean,
 *   issues: Array<{ type: string, details?: Object }>
 * }}
 */
export function validatePropertySnapshot({ propertyId, snapshot, property }) {
  const issues = [];

  // Case 1: Has propertyId but no snapshot
  if (propertyId && !snapshot) {
    issues.push({ type: SnapshotValidationIssue.MISSING_SNAPSHOT });
  }

  // Case 2: Has snapshot but wrong property ID
  if (snapshot && propertyId && snapshot.id !== propertyId) {
    issues.push({
      type: SnapshotValidationIssue.WRONG_PROPERTY_ID,
      details: { snapshotId: snapshot.id, expectedId: propertyId }
    });
  }

  // Case 3: Has snapshot but property doesn't exist (orphaned)
  if (snapshot && propertyId && !property) {
    issues.push({
      type: SnapshotValidationIssue.ORPHANED_SNAPSHOT,
      details: { propertyId }
    });
  }

  // Case 4: Snapshot data doesn't match property data (stale)
  if (snapshot && property) {
    const drift = computeSnapshotDrift(snapshot, property);
    if (drift.length > 0) {
      issues.push({
        type: SnapshotValidationIssue.STALE_DATA,
        details: { driftingFields: drift }
      });
    }
  }

  return {
    valid: issues.length === 0,
    issues
  };
}

/**
 * Summarize validation results for a batch of documents.
 *
 * @param {Array<{ id: string, valid: boolean, issues: Array }>} results
 * @returns {{
 *   total: number,
 *   valid: number,
 *   invalid: number,
 *   byIssueType: Object<string, number>
 * }}
 */
export function summarizeValidationResults(results) {
  const summary = {
    total: results.length,
    valid: 0,
    invalid: 0,
    byIssueType: {}
  };

  for (const result of results) {
    if (result.valid) {
      summary.valid++;
    } else {
      summary.invalid++;
      for (const issue of result.issues) {
        summary.byIssueType[issue.type] = (summary.byIssueType[issue.type] || 0) + 1;
      }
    }
  }

  return summary;
}
